<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import botFlowsAPI from 'dashboard/api/botFlows';

const router = useRouter();
const { t } = useI18n();

const flows = ref([]);
const isFetching = ref(false);
const isDeleting = ref(false);
const deleteDialogRef = ref(null);
const selectedFlow = ref(null);

const getApiErrorMessage = error => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (Array.isArray(responseData?.errors) && responseData.errors.length) {
    return responseData.errors.join(', ');
  }

  if (responseData?.errors && typeof responseData.errors === 'object') {
    return Object.values(responseData.errors).flat().filter(Boolean).join(', ');
  }

  return '';
};

const tableHeaders = computed(() => [
  '#',
  t('FLOW_BUILDER.TABLE.NAME'),
  t('FLOW_BUILDER.TABLE.STATUS'),
  t('FLOW_BUILDER.TABLE.INBOX'),
  t('FLOW_BUILDER.TABLE.UPDATED_AT'),
  t('FLOW_BUILDER.TABLE.ACTIONS'),
]);

const fetchFlows = async () => {
  isFetching.value = true;

  try {
    const response = await botFlowsAPI.get();
    flows.value = response.data;
  } catch (error) {
    flows.value = [];
    useAlert(getApiErrorMessage(error) || t('FLOW_BUILDER.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const openCreate = () => {
  router.push({ name: 'flow_builder_new' });
};

const openEdit = flow => {
  router.push({ name: 'flow_builder_edit', params: { id: flow.id } });
};

const exportFlow = async flow => {
  const response = await botFlowsAPI.export(flow.id);
  const blob = new Blob([JSON.stringify(response.data, null, 2)], {
    type: 'application/json',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${flow.name || 'bot-flow'}.json`;
  link.click();
  window.URL.revokeObjectURL(url);
};

const openDeleteDialog = flow => {
  selectedFlow.value = flow;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  if (!selectedFlow.value) return;

  isDeleting.value = true;

  try {
    await botFlowsAPI.delete(selectedFlow.value.id);
    useAlert(t('FLOW_BUILDER.DELETE_SUCCESS'));
    deleteDialogRef.value.close();
    selectedFlow.value = null;
    fetchFlows();
  } catch (error) {
    useAlert(getApiErrorMessage(error) || t('FLOW_BUILDER.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

onMounted(fetchFlows);
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-surface-1 p-6 sm:py-8 lg:px-16">
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-8">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-xl font-medium tracking-tight text-n-slate-12">
            {{ $t('FLOW_BUILDER.HEADER') }}
          </h1>
          <p class="mt-2 max-w-3xl text-sm text-n-slate-11">
            {{ $t('FLOW_BUILDER.INDEX_DESCRIPTION') }}
          </p>
        </div>

        <Button
          icon="i-lucide-circle-plus"
          :label="$t('FLOW_BUILDER.CREATE')"
          @click="openCreate"
        />
      </div>

      <div class="rounded-3xl border border-n-weak bg-white shadow-sm">
        <div
          v-if="isFetching"
          class="flex min-h-[240px] items-center justify-center text-n-slate-11"
        >
          {{ $t('FLOW_BUILDER.LOADING_LIST') }}
        </div>

        <div
          v-else-if="!flows.length"
          class="flex min-h-[240px] flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <p class="max-w-md text-sm text-n-slate-11">
            {{ $t('FLOW_BUILDER.EMPTY') }}
          </p>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('FLOW_BUILDER.CREATE')"
            @click="openCreate"
          />
        </div>

        <table v-else class="min-w-full divide-y divide-n-weak">
          <thead>
            <tr>
              <th
                v-for="header in tableHeaders"
                :key="header"
                class="py-4 text-left font-semibold ltr:pr-4 rtl:pl-4 text-n-slate-11"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-for="(flow, index) in flows" :key="flow.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4">{{ index + 1 }}</td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div class="flex flex-col gap-1">
                  <span class="font-medium">{{ flow.name }}</span>
                  <span class="text-sm text-n-slate-11">{{
                    flow.channel_type
                  }}</span>
                </div>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="rounded-full px-2 py-1 text-xs font-medium"
                  :class="
                    flow.status === 'published'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                  "
                >
                  {{ flow.status }}
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">{{ flow.inbox_name }}</td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                {{ new Date(flow.updated_at).toLocaleString() }}
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div class="flex items-center justify-end gap-2">
                  <Button
                    icon="i-lucide-pen"
                    xs
                    slate
                    faded
                    @click="openEdit(flow)"
                  />
                  <Button
                    icon="i-lucide-download"
                    xs
                    slate
                    faded
                    @click="exportFlow(flow)"
                  />
                  <Button
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    @click="openDeleteDialog(flow)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('FLOW_BUILDER.DELETE_CONFIRM.TITLE')"
      :description="
        $t('FLOW_BUILDER.DELETE_CONFIRM.MESSAGE', {
          name: selectedFlow?.name || '',
        })
      "
      :confirm-button-label="$t('FLOW_BUILDER.DELETE_CONFIRM.CONFIRM')"
      :cancel-button-label="$t('FLOW_BUILDER.DELETE_CONFIRM.CANCEL')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
